import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import MemoryIcon from "@mui/icons-material/Memory";
import OutputIcon from '@mui/icons-material/Output';
import InputIcon from '@mui/icons-material/Input';
import { PieChart } from "@mui/x-charts";
import { useAuth } from "../context/AuthContext";
import StreamState, { type StreamStateValue } from "./StreamState";
import { useStreamsState } from "../context/StreamsStateContext";
import type { StreamSummary } from "../types/streamSummary";
import type { PlatformSummary } from "../types/platform";
import { extractRecastOutputs, isRecastMode, type StreamOutputStatsSnapshot } from "../utils/streamOutputs";

type PieSlice = {
  id: number;
  name: string;
  value: number;
  color: string;
};

type SystemMetric = {
  label: string;
  value: string;
  detail?: string;
};

type SystemMonitorResponse = {
  metrics?: SystemMetric[];
  snapshot?: SystemMonitorSnapshot;
  error?: string;
};

type SystemMonitorSnapshot = {
  cpuPercent?: number;
  appCpuPercent?: number;
  gpuPercent?: number;
  appGpuPercent?: number;
  gpuAvailable?: boolean;
  appGpuAvailable?: boolean;
  cpuCount?: number;
  memoryPercent?: number;
  appMemPercent?: number;
  netCeilingMbit?: number;
  webRtcClients?: number | null;
  webRtcBitrate?: number | null;
};

type StreamBitrateResponse = {
  streamId: string;
  bitrates: number[];
  bitrateMeasurementsMax: number;
};

type StreamsBitratesResponse = {
  streams?: StreamBitrateResponse[];
  error?: string;
};

type StreamLastBitrateResponse = {
  streamId: string;
  bitrate?: number | null;
};

type StreamsLastBitratesResponse = {
  streams?: StreamLastBitrateResponse[];
  error?: string;
};

type StreamOutputStatusEntry = {
  playbackUrl?: string;
  metadataUrl?: string;
  segmentFormat?: string;
  sei?: import("../utils/streamOutputs").SeiStats;
  publicationState?: string;
  clientCount?: number;
  deliveryFormat?: string;
  outputId: string;
  bitrate?: number | null;
  bytesSent?: number | null;
};

type StreamOutputStatusesResponse = {
  streams?: { streamId: string; outputs?: StreamOutputStatusEntry[] }[];
  error?: string;
};

type PlatformsResponse = {
  platforms?: PlatformSummary[];
  error?: string;
};

type CpuGpuMemoryPanelProps = {
  cpuPercent: number;
  appCpuPercent: number;
  gpuPercent: number;
  appGpuPercent: number;
  gpuAvailable: boolean;
  appGpuAvailable: boolean;
  memoryPercent: number;
  appMemoryPercent: number;
};

type StreamsInPanelProps = {
  streams: StreamSummary[];
  streamBitratesById: Map<string, number[]>;
  totalInputBitrate: number;
  platformNameById: Map<string, string>;
  platformColorById: Map<string, string>;
  platformColorByName: Map<string, string>;
  fallbackPlatformColor: string;
};

type NetworkOutChartProps = {
  data: PieSlice[];
};

type NetworkOutPanelProps = {
  data: PieSlice[];
  webRtcClients?: number | null;
  webRtcBitrate?: number | null;
  udpBitrate?: number;
  udsBitrate?: number;
  srtBitrate?: number;
  totalOutputBitrate?: number;
};

type ChartConfig = {
  title: string;
  data?: PieSlice[];
  render?: () => JSX.Element;
  badgeCount?: number;
};

const DEFAULT_PLATFORM_COLOR = "#2563eb";
const MAX_TINY_BITRATE_POINTS = 60;
const OUTPUT_STATUS_REFRESH_MS = 2000;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const clampBitrateSeries = (series: number[]) => {
  if (series.length <= MAX_TINY_BITRATE_POINTS) {
    return series;
  }
  return series.slice(series.length - MAX_TINY_BITRATE_POINTS);
};

const parsePercent = (value?: string) => {
  if (!value) {
    return null;
  }
  const matches = value.match(/(\d+(?:\.\d+)?)%/g);
  if (!matches || matches.length === 0) {
    return null;
  }
  const last = matches[matches.length - 1];
  return Number(last.replace("%", ""));
};

const formatPercent = (value: number | { value: number }) => {
  const numeric = typeof value === "number" ? value : value.value;
  return `${numeric.toFixed(1)}%`;
};

const CpuGpuMemoryPanel = ({
  cpuPercent,
  appCpuPercent,
  gpuPercent,
  appGpuPercent,
  gpuAvailable,
  appGpuAvailable,
  memoryPercent,
  appMemoryPercent
}: CpuGpuMemoryPanelProps) => {
  const theme = useTheme();
  const highlightColor = theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark;
  const safeCpu = clamp(cpuPercent, 0, 100);
  const safeAppCpu = clamp(appCpuPercent, 0, safeCpu);
  const otherCpu = clamp(safeCpu - safeAppCpu, 0, 100);
  const safeGpu = clamp(gpuPercent, 0, 100);
  const safeAppGpu = clamp(appGpuPercent, 0, safeGpu);
  const otherGpu = clamp(safeGpu - safeAppGpu, 0, 100);
  const safeMemory = clamp(memoryPercent, 0, 100);
  const safeAppMemory = clamp(appMemoryPercent, 0, safeMemory);
  const otherMemory = clamp(safeMemory - safeAppMemory, 0, 100);
  const cpuAlert = safeCpu >= 85;
  const gpuAlert = safeGpu >= 85;
  const memoryAlert = safeMemory >= 85;
  const cpuPrimaryColor = cpuAlert ? theme.palette.error.main : theme.palette.primary.main;
  const cpuSecondaryColor = cpuAlert ? theme.palette.error.light : theme.palette.warning.main;
  const gpuPrimaryColor = gpuAlert ? theme.palette.error.main : theme.palette.success.main;
  const gpuSecondaryColor = gpuAlert ? theme.palette.error.light : theme.palette.info.main;
  const memoryPrimaryColor = memoryAlert ? theme.palette.error.main : theme.palette.secondary.main;
  const memorySecondaryColor = memoryAlert ? theme.palette.error.light : theme.palette.info.main;

  return (
    <Stack spacing={1} sx={{ width: 260, alignItems: "center" }}>
      <Box sx={{ position: "relative", width: 260, height: 260 }}>
        <PieChart
          series={[
            {
              data: [
                { id: 0, value: safeAppCpu, label: "SkyView CPU", color: cpuPrimaryColor },
                { id: 1, value: otherCpu, label: "Others", color: cpuSecondaryColor },
                { id: 2, value: 100 - safeCpu, label: "Idle", color: theme.palette.grey[600] }
              ],
              innerRadius: 76,
              outerRadius: 104,
              paddingAngle: 2,
              highlightScope: { fade: "global", highlight: "item" },
              faded: { additionalRadius: -10, color: theme.palette.grey[400] },
              highlighted: { additionalRadius: 6 },
              valueFormatter: formatPercent
            },
            {
              data: [
                { id: 5, value: safeAppGpu, label: "SkyView GPU", color: gpuPrimaryColor },
                { id: 6, value: otherGpu, label: "Other GPU", color: gpuSecondaryColor },
                { id: 7, value: 100 - safeGpu, label: "GPU idle", color: theme.palette.grey[600] }
              ],
              innerRadius: 52,
              outerRadius: 72,
              paddingAngle: 2,
              highlightScope: { fade: "global", highlight: "item" },
              faded: { additionalRadius: -8, color: theme.palette.grey[400] },
              highlighted: { additionalRadius: 4 },
              valueFormatter: formatPercent
            },
            {
              data: [
                { id: 2, value: safeAppMemory, label: "SkyView memory", color: memoryPrimaryColor },
                { id: 3, value: otherMemory, label: "Other memory", color: memorySecondaryColor },
                { id: 4, value: 100 - safeMemory, label: "Memory free", color: theme.palette.grey[600] }
              ],
              innerRadius: 28,
              outerRadius: 48,
              paddingAngle: 2,
              highlightScope: { fade: "global", highlight: "item" },
              faded: { additionalRadius: -10, color: theme.palette.grey[400] },
              highlighted: { additionalRadius: 6 },
              valueFormatter: formatPercent
            }
          ]}
          height={260}
          width={260}
          margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
          slots={{ legend: () => null }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none"
          }}
        >
          <MemoryIcon sx={{ fontSize: 28, color: theme.palette.text.secondary }} />
        </Box>
      </Box>
      <Box sx={{ width: "100%" }}>
        <Table size="small" sx={{ "& td": { borderBottom: "none", px: 0, py: 0.25 } }}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  SkyView CPU
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatPercent(safeAppCpu)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  SkyView GPU
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {appGpuAvailable ? formatPercent(safeAppGpu) : "N/A"}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  SkyView Memory
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatPercent(safeAppMemory)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Total CPU
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatPercent(safeCpu)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Total GPU
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {gpuAvailable ? formatPercent(safeGpu) : "N/A"}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Total Memory
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatPercent(safeMemory)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};

const StreamsInPanel = ({
  streams,
  streamBitratesById,
  totalInputBitrate,
  platformNameById,
  platformColorById,
  platformColorByName,
  fallbackPlatformColor
}: StreamsInPanelProps) => {
  const theme = useTheme();
  const highlightColor = theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark;
  const { getStreamState } = useStreamsState();
  const streamColors = useMemo(
    () => [
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.grey[500],
      theme.palette.grey[700]
    ],
    [theme]
  );
  const platformGroups = useMemo(() => {
    const knownIds = new Set<string>();
    const rows = streams.map((stream) => {
      knownIds.add(stream.id);
      const bitrates = streamBitratesById.get(stream.id) ?? [];
      const latest = bitrates.length ? bitrates[bitrates.length - 1] : 0;
      return {
        id: stream.id,
        name: stream.sensor || stream.id,
        latest,
        platformName: stream.platformName,
        platformId: stream.platformId
      };
    });
    streamBitratesById.forEach((bitrates, streamId) => {
      if (knownIds.has(streamId)) {
        return;
      }
      const latest = bitrates.length ? bitrates[bitrates.length - 1] : 0;
      rows.push({
        id: streamId,
        name: streamId,
        latest,
        platformName: undefined,
        platformId: undefined
      });
    });

    const groups = new Map<string, { key: string; name: string; color: string; total: number; streams: typeof rows }>();
    rows.forEach((row) => {
      const platformId = row.platformId?.trim() || "";
      const resolvedName =
        (platformId && platformNameById.get(platformId)) ||
        (row.platformName?.trim() ? row.platformName.trim() : "") ||
        "Unknown";
      const resolvedColor =
        (platformId && platformColorById.get(platformId)) ||
        (resolvedName && platformColorByName.get(resolvedName)) ||
        fallbackPlatformColor;
      const key = platformId || resolvedName;
      const existing = groups.get(key);
      if (existing) {
        existing.total += row.latest;
        existing.streams.push(row);
      } else {
        groups.set(key, { key, name: resolvedName, color: resolvedColor, total: row.latest, streams: [row] });
      }
    });

    return Array.from(groups.values())
      .filter((entry) => entry.total > 0)
      .map((entry) => ({
        ...entry,
        streams: entry.streams
          .filter((stream) => stream.latest > 0)
          .sort((a, b) => b.latest - a.latest)
      }))
      .sort((a, b) => b.total - a.total);
  }, [streams, streamBitratesById, platformNameById, platformColorById, platformColorByName, fallbackPlatformColor]);

  const totalBitrate = platformGroups.reduce((sum, group) => sum + group.total, 0);
  const platformSlices = platformGroups.map((group, index) => ({
    id: index,
    name: group.name,
    value: group.total,
    color: group.color
  }));

  const streamSlices = useMemo(() => {
    let counter = 0;
    return platformGroups.flatMap((group) =>
      group.streams.map((stream) => ({
        id: counter++,
        name: stream.name,
        value: stream.latest,
        color: group.color
      }))
    );
  }, [platformGroups]);

  const streamStateCounts = useMemo(() => {
    const counts: Record<StreamStateValue, number> = {
      connecting: 0,
      online: 0,
      timeout: 0,
      error: 0,
      offline: 0,
      unknown: 0
    };
    streams.forEach((stream) => {
      const state = getStreamState(stream.id);
      counts[state] = (counts[state] ?? 0) + 1;
    });
    return counts;
  }, [streams, getStreamState]);

  const hasBitrateData = streamSlices.length > 0 && totalBitrate > 0;

  const formatStreamPercent = (value: number | { value: number }) => {
    const numeric = typeof value === "number" ? value : value.value;
    const percent = totalBitrate > 0 ? (numeric / totalBitrate) * 100 : 0;
    return `${percent.toFixed(1)}% (${numeric.toFixed(2)} Mbit/s)`;
  };

  const emptySliceColor = theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300];
  const emptySeries = [{ id: 0, value: 1, label: "No data", color: emptySliceColor }];
  const emptyValueFormatter = () => "No data";

  return (
    <Stack spacing={1.5} sx={{ alignItems: "center" }}>
      <Box sx={{ position: "relative", width: 260, height: 260 }}>
        <PieChart
          series={[
            {
              data: hasBitrateData
                ? platformSlices.map((slice) => ({
                    id: slice.id,
                    value: slice.value,
                    label: slice.name,
                    color: slice.color
                  }))
                : emptySeries,
              innerRadius: 36,
              outerRadius: 62,
              paddingAngle: 2,
              highlightScope: { fade: "global", highlight: "item" },
              faded: { additionalRadius: -10, color: theme.palette.grey[400] },
              highlighted: { additionalRadius: 6 },
              valueFormatter: hasBitrateData ? formatStreamPercent : emptyValueFormatter
            },
            {
              data: hasBitrateData
                ? streamSlices.map((slice) => ({
                    id: slice.id,
                    value: slice.value,
                    label: slice.name,
                    color: slice.color
                  }))
                : emptySeries,
              innerRadius: 66,
              outerRadius: 86,
              paddingAngle: 2,
              highlightScope: { fade: "global", highlight: "item" },
              faded: { additionalRadius: -10, color: theme.palette.grey[400] },
              highlighted: { additionalRadius: 6 },
              valueFormatter: hasBitrateData ? formatStreamPercent : emptyValueFormatter
            }
          ]}
          height={260}
          width={260}
          margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
          slots={{ legend: () => null }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none"
          }}
        >
          <InputIcon sx={{ fontSize: 28, color: theme.palette.text.secondary }} />
        </Box>
      </Box>
      <Box sx={{ width: "100%", maxWidth: 210 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <StreamState state="online" size={12} />
            <Typography variant="body2" color="text.secondary">
              Online
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {streamStateCounts.online}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <StreamState state="timeout" size={12} />
            <Typography variant="body2" color="text.secondary">
              Timeout
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {streamStateCounts.timeout}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <StreamState state="error" size={12} />
            <Typography variant="body2" color="text.secondary">
              Error
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {streamStateCounts.error}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <StreamState state="offline" size={12} />
            <Typography variant="body2" color="text.secondary">
              Offline
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {streamStateCounts.offline}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <StreamState state="unknown" size={12} />
            <Typography variant="body2" color="text.secondary">
              Unknown
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {streamStateCounts.unknown}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Total input
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
              {formatTotalInputBitrate(totalInputBitrate)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

const NetworkOutChart = ({ data }: NetworkOutChartProps) => {
  const theme = useTheme();
  const slices = data ?? [];
  const total = slices.reduce((acc, slice) => {
    const value = typeof slice.value === "number" && Number.isFinite(slice.value) ? slice.value : 0;
    return acc + value;
  }, 0);
  const hasData = total > 0;
  const chartSlices = hasData
    ? slices
    : [{ id: "empty", name: "", value: 1, color: theme.palette.action.disabledBackground }];
  const formatBitrateMbit = (value: number | { value: number }) => {
    if (!hasData) {
      return "0.00 Mbit/s";
    }
    const numeric = typeof value === "number" ? value : value.value;
    return `${numeric.toFixed(2)} Mbit/s`;
  };

  return (
    <Box sx={{ position: "relative", width: 260, height: 260 }}>
      <PieChart
        series={[
        {
          data: chartSlices.map((slice) => ({
            id: slice.id,
            value: slice.value,
            label: hasData ? slice.name : "",
            color: slice.color
          })),
          innerRadius: 58,
          outerRadius: 90,
          paddingAngle: 2,
          highlightScope: { fade: "global", highlight: "item" },
          faded: { additionalRadius: -10, color: theme.palette.grey[400] },
          highlighted: { additionalRadius: 6 },
          valueFormatter: formatBitrateMbit
        }
        ]}
        height={260}
        width={260}
        margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
        slots={{ legend: () => null }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none"
        }}
      >
        <OutputIcon sx={{ fontSize: 28, color: theme.palette.text.secondary }} />
      </Box>
    </Box>
  );
};

const formatWebRtcClients = (value?: number | null) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "—";
  }
  return `${Math.max(0, Math.round(value))}`;
};

const formatWebRtcBitrate = (value?: number | null) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "—";
  }
  const mbitPerSec = Math.max(0, value) / 1_000_000;
  return `${mbitPerSec.toFixed(2)} Mbit/s`;
};

const formatUdpBitrate = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0.00 Mbit/s";
  }
  return `${Math.max(0, value).toFixed(2)} Mbit/s`;
};

function formatTotalInputBitrate(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0.00 Mbit/s";
  }
  return `${Math.max(0, value).toFixed(2)} Mbit/s`;
}

const formatTotalOutputBitrate = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0.00 Mbit/s";
  }
  return `${Math.max(0, value).toFixed(2)} Mbit/s`;
};

const NetworkOutPanel = ({
  data,
  webRtcClients,
  webRtcBitrate,
  udpBitrate,
  udsBitrate,
  srtBitrate,
  totalOutputBitrate
}: NetworkOutPanelProps) => {
  const theme = useTheme();
  const highlightColor = theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark;

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <NetworkOutChart data={data} />
      </Box>
      <Box sx={{ maxWidth: 280, alignSelf: "center", width: "100%" }}>
        <Table size="small" sx={{ "& td": { borderBottom: "none", px: 0, py: 0.25 } }}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  WebRTC clients
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatWebRtcClients(webRtcClients)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  WebRTC bitrate
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatWebRtcBitrate(webRtcBitrate)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Udp Recast
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatUdpBitrate(udpBitrate)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Tooltip title="Unix Domain Socket is not sent over the network" placement="top">
                  <Typography variant="body2" color="text.secondary">
                    Uds Recast
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatUdpBitrate(udsBitrate)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Srt Recast
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatUdpBitrate(srtBitrate)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Total output
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: highlightColor }}>
                  {formatTotalOutputBitrate(totalOutputBitrate)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};

const SystemMonitorServer = () => {
  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setAvailableWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  // Scale the complete layout, including text and spacing, to the actual panel
  // width (which can be narrower than the viewport when the sidebar is open).
  const stackedPanels = availableWidth < 560;
  const panelsWidth = stackedPanels ? 320 : 1040;
  const panelsScale = availableWidth > 0 ? Math.min(1, availableWidth / panelsWidth) : 1;
  const { authorizedFetch, resolveApiPath } = useAuth();
  const theme = useTheme();
  const [metrics, setMetrics] = useState<SystemMetric[] | null>(null);
  const [snapshot, setSnapshot] = useState<SystemMonitorSnapshot | null>(null);
  const [streams, setStreams] = useState<StreamSummary[]>([]);
  const [streamBitratesById, setStreamBitratesById] = useState<Map<string, number[]>>(() => new Map());
  const [platforms, setPlatforms] = useState<PlatformSummary[]>([]);
  const [streamOutputStatsById, setStreamOutputStatsById] = useState<
    Map<string, Map<string, StreamOutputStatsSnapshot>>
  >(() => new Map());

  const fetchSystemMonitor = useCallback(
    async (isActive: () => boolean, options?: { background?: boolean }) => {
      const background = Boolean(options?.background);
      try {
        const response = await authorizedFetch(resolveApiPath("/api/server/system-monitor"));
        const payload = (await response.json().catch(() => null)) as SystemMonitorResponse | null;

        if (!response.ok) {
          throw new Error(payload && typeof payload.error === "string" ? payload.error : "Unable to load metrics");
        }

        if (!isActive()) {
          return;
        }

        setMetrics(payload?.metrics ?? []);
        setSnapshot(payload?.snapshot ?? null);
      } catch (err) {
        if (!isActive()) {
          return;
        }
        if (!background) {
          setMetrics(null);
          setSnapshot(null);
        }
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  useEffect(() => {
    let active = true;
    void fetchSystemMonitor(() => active);
    const intervalId = window.setInterval(() => {
      void fetchSystemMonitor(() => active, { background: true });
    }, 1000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [fetchSystemMonitor]);

  const lastBitrateRefreshMs = useMemo(() => {
    if (streams.length <= 25) {
      return 2000;
    }
    if (streams.length <= 50) {
      return 3000;
    }
    return 4000;
  }, [streams.length]);

  const fetchStreams = useCallback(
    async (isActive: () => boolean) => {
      try {
        const response = await authorizedFetch(resolveApiPath("/api/streams"));
        const payload = (await response.json().catch(() => null)) as { streams?: StreamSummary[]; error?: string } | null;

        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load streams");
        }

        if (!isActive()) {
          return;
        }

        setStreams(payload?.streams ?? []);
      } catch (err) {
        if (!isActive()) {
          return;
        }
        setStreams([]);
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  const fetchStreamBitrates = useCallback(
    async (isActive: () => boolean, options?: { background?: boolean }) => {
      const background = Boolean(options?.background);
      try {
        const response = await authorizedFetch(resolveApiPath("/api/streams/bitrates"));
        const payload = (await response.json().catch(() => null)) as StreamsBitratesResponse | null;

        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load stream bitrates");
        }

        if (!isActive()) {
          return;
        }

        const entries = payload?.streams ?? [];
        setStreamBitratesById((previous) => {
          const next = new Map(previous);
          let updated = false;
          entries.forEach((entry) => {
            const series = clampBitrateSeries((entry.bitrates ?? []).map((value) => Number(value)));
            const existing = next.get(entry.streamId);
            const shouldReplace = !existing || existing.length < series.length;
            if (shouldReplace) {
              next.set(entry.streamId, series);
              updated = true;
            }
          });
          return updated ? next : previous;
        });
      } catch (err) {
        if (!isActive() || background) {
          return;
        }
        setStreamBitratesById(new Map());
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  const fetchStreamLastBitrates = useCallback(
    async (isActive: () => boolean) => {
      try {
        const response = await authorizedFetch(resolveApiPath("/api/streams/last_bitrates"));
        const payload = (await response.json().catch(() => null)) as StreamsLastBitratesResponse | null;

        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load latest stream bitrates");
        }

        if (!isActive()) {
          return;
        }

        const entries = payload?.streams ?? [];
        const latestById = new Map<string, number>();
        entries.forEach(({ streamId, bitrate }) => {
          latestById.set(streamId, typeof bitrate === "number" ? bitrate : 0);
        });

        setStreamBitratesById((previous) => {
          const next = new Map(previous);
          let updated = false;
          latestById.forEach((value, streamId) => {
            const existing = next.get(streamId) ?? [];
            const truncated = clampBitrateSeries([...existing, value]);
            next.set(streamId, truncated);
            updated = true;
          });
          return updated ? next : previous;
        });
      } catch (err) {
        if (!isActive()) {
          return;
        }
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  const hasRecastStreams = useMemo(() => streams.some((stream) => isRecastMode(stream.mode)), [streams]);

  const fetchStreamOutputStats = useCallback(
    async (isActive: () => boolean, options?: { background?: boolean }) => {
      const background = Boolean(options?.background);
      if (!hasRecastStreams) {
        if (isActive() && !background) {
          setStreamOutputStatsById(new Map());
        }
        return;
      }
      try {
        const response = await authorizedFetch(resolveApiPath("/api/streams/output_stats"));
        const payload = (await response.json().catch(() => null)) as StreamOutputStatusesResponse | null;

        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load stream output stats");
        }

        if (!isActive()) {
          return;
        }

        const entries = payload?.streams ?? [];
        setStreamOutputStatsById(() => {
          const next = new Map<string, Map<string, StreamOutputStatsSnapshot>>();
          entries.forEach((entry) => {
            const streamId = typeof entry?.streamId === "string" ? entry.streamId.trim() : "";
            if (!streamId) {
              return;
            }
            const outputs = new Map<string, StreamOutputStatsSnapshot>();
            (entry.outputs ?? []).forEach((output) => {
              const outputId = typeof output.outputId === "string" ? output.outputId.trim() : "";
              if (!outputId) {
                return;
              }
              const bitrate = Number(output.bitrate ?? 0);
              const bytesSent = typeof output.bytesSent === "number" ? output.bytesSent : 0;
              outputs.set(outputId, {
                playbackUrl: output.playbackUrl, metadataUrl: output.metadataUrl,
                sei: output.sei, segmentFormat: output.segmentFormat, publicationState: output.publicationState, clientCount: output.clientCount, deliveryFormat: output.deliveryFormat,
                bitrate: Number.isFinite(bitrate) ? bitrate : 0,
                bytesSent
              });
            });
            next.set(streamId, outputs);
          });
          return next;
        });
      } catch (err) {
        if (!isActive()) {
          return;
        }
        if (!background) {
          setStreamOutputStatsById(new Map());
        }
      }
    },
    [authorizedFetch, resolveApiPath, hasRecastStreams]
  );

  const fetchPlatforms = useCallback(
    async (isActive: () => boolean) => {
      try {
        const response = await authorizedFetch(resolveApiPath("/api/platforms"));
        const payload = (await response.json().catch(() => null)) as PlatformsResponse | null;

        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to load platforms");
        }

        if (!isActive()) {
          return;
        }

        setPlatforms(payload?.platforms ?? []);
      } catch (err) {
        if (!isActive()) {
          return;
        }
        setPlatforms([]);
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  useEffect(() => {
    let active = true;
    void fetchStreams(() => active);
    return () => {
      active = false;
    };
  }, [fetchStreams]);

  useEffect(() => {
    let active = true;
    void fetchStreamBitrates(() => active);
    return () => {
      active = false;
    };
  }, [fetchStreamBitrates]);

  useEffect(() => {
    let active = true;
    void fetchPlatforms(() => active);
    return () => {
      active = false;
    };
  }, [fetchPlatforms]);

  useEffect(() => {
    let active = true;
    void fetchStreamLastBitrates(() => active);
    const intervalId = window.setInterval(() => {
      void fetchStreamLastBitrates(() => active);
    }, lastBitrateRefreshMs);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [fetchStreamLastBitrates, lastBitrateRefreshMs]);

  useEffect(() => {
    let active = true;
    void fetchStreamOutputStats(() => active);
    const intervalId = window.setInterval(() => {
      void fetchStreamOutputStats(() => active, { background: true });
    }, OUTPUT_STATUS_REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [fetchStreamOutputStats]);

  const normalizedMetrics = useMemo(
    () =>
      (metrics ?? []).filter(
        (metric) =>
          typeof metric?.label === "string" &&
          metric.label.trim().length > 0 &&
          typeof metric?.value === "string" &&
          metric.value.trim().length > 0
      ),
    [metrics]
  );

  const metricMap = useMemo(() => {
    const map = new Map<string, SystemMetric>();
    normalizedMetrics.forEach((metric) => {
      map.set(metric.label.toLowerCase(), metric);
    });
    return map;
  }, [normalizedMetrics]);

  const cpuMetric = metricMap.get("cpu usage");
  const appCpuMetric = metricMap.get("skyview cpu usage");
  const memoryMetric = metricMap.get("memory usage");

  const cpuPercent = snapshot?.cpuPercent ?? parsePercent(cpuMetric?.value) ?? 0;
  const cpuCount = snapshot?.cpuCount ?? 0;
  const appCpuRaw = snapshot?.appCpuPercent;
  const appCpuPercent =
    typeof appCpuRaw === "number"
      ? (cpuCount > 0 ? appCpuRaw / cpuCount : appCpuRaw)
      : parsePercent(appCpuMetric?.value) ?? 0;
  const gpuPercent = snapshot?.gpuPercent ?? parsePercent(metricMap.get("gpu usage")?.value) ?? 0;
  const appGpuPercent = snapshot?.appGpuPercent ?? parsePercent(metricMap.get("skyview gpu usage")?.value) ?? 0;
  const appGpuAvailable = snapshot?.appGpuAvailable ?? (parsePercent(metricMap.get("skyview gpu usage")?.value) !== null);
  const gpuAvailable = snapshot?.gpuAvailable ?? metricMap.has("gpu usage");
  const memoryPercent = snapshot?.memoryPercent ?? parsePercent(memoryMetric?.value) ?? 0;
  const appMemoryPercent = snapshot?.appMemPercent ?? parsePercent(metricMap.get("skyview memory usage")?.value) ?? 0;
  const platformNameById = useMemo(() => {
    const map = new Map<string, string>();
    platforms.forEach((platform) => {
      if (platform?.id) {
        map.set(platform.id, platform.name?.trim() || platform.id);
      }
    });
    return map;
  }, [platforms]);

  const platformColorById = useMemo(() => {
    const map = new Map<string, string>();
    platforms.forEach((platform) => {
      const color = platform.color?.trim() || DEFAULT_PLATFORM_COLOR;
      if (platform?.id) {
        map.set(platform.id, color);
      }
    });
    return map;
  }, [platforms]);

  const platformColorByName = useMemo(() => {
    const map = new Map<string, string>();
    platforms.forEach((platform) => {
      const color = platform.color?.trim() || DEFAULT_PLATFORM_COLOR;
      if (platform?.name?.trim()) {
        map.set(platform.name.trim(), color);
      }
    });
    return map;
  }, [platforms]);

  const totalInputBitrate = useMemo(() => {
    let total = 0;
    streamBitratesById.forEach((series) => {
      if (!series.length) {
        return;
      }
      const latest = series[series.length - 1];
      if (typeof latest === "number" && Number.isFinite(latest)) {
        total += latest;
      }
    });
    return total;
  }, [streamBitratesById]);

  const udpBitrateTotal = useMemo(() => {
    if (!hasRecastStreams) {
      return 0;
    }
    let total = 0;
    streams.forEach((stream) => {
      if (!isRecastMode(stream.mode)) {
        return;
      }
      const outputs = extractRecastOutputs(stream.targets);
      if (!outputs.length) {
        return;
      }
      const statsById = streamOutputStatsById.get(stream.id);
      outputs.forEach((output) => {
        const outputType = (output.type ?? "").trim().toLowerCase();
        if (outputType !== "udp") {
          return;
        }
        const stats = statsById?.get(output.id);
        if (typeof stats?.bitrate === "number" && Number.isFinite(stats.bitrate)) {
          total += stats.bitrate;
        }
      });
    });
    return total;
  }, [hasRecastStreams, streams, streamOutputStatsById]);

  const udsBitrateTotal = useMemo(() => {
    if (!hasRecastStreams) {
      return 0;
    }
    let total = 0;
    streams.forEach((stream) => {
      if (!isRecastMode(stream.mode)) {
        return;
      }
      const outputs = extractRecastOutputs(stream.targets);
      if (!outputs.length) {
        return;
      }
      const statsById = streamOutputStatsById.get(stream.id);
      outputs.forEach((output) => {
        const outputType = (output.type ?? "").trim().toLowerCase();
        if (outputType !== "uds") {
          return;
        }
        const stats = statsById?.get(output.id);
        if (typeof stats?.bitrate === "number" && Number.isFinite(stats.bitrate)) {
          total += stats.bitrate;
        }
      });
    });
    return total;
  }, [hasRecastStreams, streams, streamOutputStatsById]);

  const srtBitrateTotal = useMemo(() => {
    if (!hasRecastStreams) {
      return 0;
    }
    let total = 0;
    streams.forEach((stream) => {
      if (!isRecastMode(stream.mode)) {
        return;
      }
      const outputs = extractRecastOutputs(stream.targets);
      if (!outputs.length) {
        return;
      }
      const statsById = streamOutputStatsById.get(stream.id);
      outputs.forEach((output) => {
        const outputType = (output.type ?? "").trim().toLowerCase();
        if (outputType !== "srt") {
          return;
        }
        const stats = statsById?.get(output.id);
        if (typeof stats?.bitrate === "number" && Number.isFinite(stats.bitrate)) {
          total += stats.bitrate;
        }
      });
    });
    return total;
  }, [hasRecastStreams, streams, streamOutputStatsById]);

  const webRtcBitrateMbit =
    typeof snapshot?.webRtcBitrate === "number" && Number.isFinite(snapshot.webRtcBitrate)
      ? Math.max(0, snapshot.webRtcBitrate) / 1_000_000
      : 0;

  const networkOutData: PieSlice[] = [
    { id: 0, name: "WebRTC", value: webRtcBitrateMbit, color: theme.palette.info.main },
    { id: 1, name: "UDP", value: udpBitrateTotal, color: theme.palette.warning.main },
    { id: 2, name: "UDS", value: udsBitrateTotal, color: theme.palette.success.main },
    { id: 3, name: "SRT", value: srtBitrateTotal, color: theme.palette.error.main }
  ];

  const totalOutputBitrate = webRtcBitrateMbit + udpBitrateTotal + udsBitrateTotal + srtBitrateTotal;

  const chartConfigs: ChartConfig[] = [
    {
      title: "Inputs  ",
      data: [],
      badgeCount: streams.length,
      render: () => (
        <StreamsInPanel
          streams={streams}
          streamBitratesById={streamBitratesById}
          totalInputBitrate={totalInputBitrate}
          platformNameById={platformNameById}
          platformColorById={platformColorById}
          platformColorByName={platformColorByName}
          fallbackPlatformColor={DEFAULT_PLATFORM_COLOR}
        />
      )
    },
    {
      title: "CPU / GPU / Memory",
      data: [],
      render: () => (
        <CpuGpuMemoryPanel
          cpuPercent={cpuPercent}
          appCpuPercent={appCpuPercent}
          gpuPercent={gpuPercent}
          appGpuPercent={appGpuPercent}
          gpuAvailable={gpuAvailable}
          appGpuAvailable={appGpuAvailable}
          memoryPercent={memoryPercent}
          appMemoryPercent={appMemoryPercent}
        />
      )
    },
    {
      title: "Outputs",
      data: networkOutData,
      render: () => (
        <NetworkOutPanel
          data={networkOutData}
          webRtcClients={snapshot?.webRtcClients ?? null}
          webRtcBitrate={snapshot?.webRtcBitrate ?? null}
          udpBitrate={udpBitrateTotal}
          udsBitrate={udsBitrateTotal}
          srtBitrate={srtBitrateTotal}
          totalOutputBitrate={totalOutputBitrate}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 2, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Paper ref={panelsContainerRef} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        <Stack
          direction={stackedPanels ? "column" : "row"}
          spacing={2}
          sx={{
            alignItems: "stretch",
            width: panelsWidth,
            maxWidth: "none",
            mx: "auto",
            zoom: panelsScale,
            visibility: availableWidth > 0 ? "visible" : "hidden"
          }}
        >
          {chartConfigs.map((chart, index) => (
            <Box
              key={chart.title}
              sx={{
                display: "grid",
                gridTemplateColumns: !stackedPanels && index < chartConfigs.length - 1 ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)",
                gridTemplateRows: "auto 1fr",
                columnGap: 1,
                flex: 1,
                minWidth: 0,
                p: 1
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, gridColumn: "1 / -1" }}>
                <Typography variant="subtitle1">{chart.title}</Typography>
                {typeof chart.badgeCount === "number" ? (
                  <Chip label={chart.badgeCount} size="small" variant="outlined" />
                ) : null}
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: chart.render ? "auto" : 260,
                  minHeight: 260,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center"
                }}
              >
                {chart.render ? (
                  chart.render()
                ) : (
                  <PieChart
                    series={[
                      {
                        data: (chart.data ?? []).map((slice) => ({
                          id: slice.id,
                          value: slice.value,
                          label: slice.name,
                          color: slice.color
                        })),
                        innerRadius: 58,
                        outerRadius: 90,
                        paddingAngle: 2,
                        valueFormatter: formatPercent
                      }
                    ]}
                    height={260}
                    width={260}
                    margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    slots={{ legend: () => null }}
                  />
                )}
              </Box>
              {index < chartConfigs.length - 1 ? (
                <Box
                  sx={{
                    display: stackedPanels ? "none" : "flex",
                    alignSelf: "start",
                    height: 260,
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1
                  }}
                >
                  <DoubleArrowIcon color="action" />
                </Box>
              ) : null}
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SystemMonitorServer;
